import React, { useEffect, useState } from "react";
import { HiX } from "react-icons/hi";

interface InlineProgressProps {
  label: string;
  active: boolean;
  completed?: number;
  total?: number;
  hint?: string;
  onCancel?: () => void;
  cancelLabel?: string;
}

const formatElapsed = (ms: number) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem.toString().padStart(2, "0")}s`;
};

export const InlineProgress: React.FC<InlineProgressProps> = ({
  label,
  active,
  completed,
  total,
  hint,
  onCancel,
  cancelLabel = "Cancel",
}) => {
  const [start, setStart] = useState<number | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (active && start === null) setStart(Date.now());
    if (!active) {
      setStart(null);
      setCancelling(false);
    }
  }, [active, start]);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return null;

  const elapsedMs = start ? now - start : 0;
  const determinate = typeof total === "number" && total > 0;
  const percent =
    determinate && typeof completed === "number"
      ? Math.min(100, Math.round((completed / total!) * 100))
      : null;

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--card-container)] p-3 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-[var(--foreground)]">{label}</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--foreground-muted)] tabular-nums">
            {determinate
              ? `${completed ?? 0}/${total} · ${formatElapsed(elapsedMs)}`
              : formatElapsed(elapsedMs)}
          </span>
          {onCancel && (
            <button
              type="button"
              onClick={() => {
                setCancelling(true);
                onCancel();
              }}
              disabled={cancelling}
              className="px-2 py-1 text-xs rounded-md text-[var(--foreground-muted)] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <HiX className="w-3.5 h-3.5" />
              {cancelling ? "Cancelling…" : cancelLabel}
            </button>
          )}
        </div>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--muted)]"
        aria-hidden="true"
      >
        {determinate ? (
          <div
            className="h-full bg-[var(--accent)] transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent animate-pulse" />
        )}
      </div>
      {hint && (
        <p className="text-xs text-[var(--foreground-muted)]">{hint}</p>
      )}
    </div>
  );
};
